import React, { useState } from 'react';
import { X } from 'lucide-react';
import { carBrands } from '../data/mock';

const PortfolioSection = () => {
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const openGallery = (brand) => {
    setSelectedBrand(brand);
    setSelectedImageIndex(0);
  };

  const closeGallery = () => {
    setSelectedBrand(null);
    setSelectedImageIndex(0);
  };

  const nextImage = () => {
    if (selectedBrand) {
      setSelectedImageIndex((prev) => (prev + 1) % selectedBrand.images.length);
    }
  };

  const prevImage = () => {
    if (selectedBrand) {
      setSelectedImageIndex(
        (prev) => (prev - 1 + selectedBrand.images.length) % selectedBrand.images.length
      );
    }
  };

  return (
    <section
      id="portfolio"
      className="section-spacing-large"
      style={{ backgroundColor: 'var(--color-carbon)' }}>

      <div className="container-artworld">
        {/* Section Title */}
        <div className="text-center mb-16">
          <p className="label-text mb-4">Découvrez</p>
          <h2 className="section-title">Portfolio Premium</h2>
          <p className="body-text max-w-3xl mx-auto mt-6">
            Explorez notre collection de photographies automobiles classées par marque. 
            Chaque marque représente l'excellence et la passion automobile.
          </p>
          <div
            className="w-24 h-1 mx-auto mt-6"
            style={{
              background: 'linear-gradient(90deg, transparent, var(--color-red-accent), transparent)'
            }} />

        </div>

        {/* Brands Grid */}
        <div className="grid-brands">
          {carBrands.map((brand) =>
          <button
            key={brand.id}
            onClick={() => openGallery(brand)}
            className="group relative overflow-hidden"
            style={{
              aspectRatio: '1',
              background: 'var(--color-black)',
              border: '1px solid var(--color-dark-gray)',
              cursor: 'pointer',
              transition: 'all var(--transition-normal)'
            }}>

              {/* Background Image */}
              <img
              src={brand.images[0]}
              alt={brand.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy" />


              {/* Overlay */}
              <div
              className="absolute inset-0 bg-black bg-opacity-60 group-hover:bg-opacity-40 transition-all duration-300" />


              {/* Brand Name */}
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 className="brand-name text-center px-4">{brand.name}</h3>
              </div>

              {/* Hover Border Effect */}
              <div
              className="absolute inset-0 group-hover:opacity-100 transition-opacity duration-300 !opacity-[0%] !shadow-lg"
              style={{
                border: '2px solid var(--color-red-accent)'
              }} />

            </button>
          )}
        </div>
      </div>

      {/* Fullscreen Gallery Modal */}
      {selectedBrand &&
      <div
        className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
        onClick={closeGallery}>

          {/* Close Button */}
          <button
          onClick={closeGallery}
          className="absolute top-6 right-6 text-white hover:text-red-500 transition-colors duration-300"
          style={{
            background: 'rgba(26, 26, 26, 0.8)',
            border: 'none',
            padding: '1rem',
            borderRadius: '50%',
            cursor: 'pointer',
            zIndex: 1000
          }}>

            <X size={32} />
          </button>

          {/* Brand Name */}
          <div className="absolute top-6 left-6 z-[var(--z-modal)]">
            <h2 className="brand-name">{selectedBrand.name}</h2>
            <p className="caption-text mt-2">
              Image {selectedImageIndex + 1} sur {selectedBrand.images.length}
            </p>
          </div>

          {/* Main Image */}
          <div
          className="relative max-w-6xl max-h-[80vh] mx-auto"
          onClick={(e) => e.stopPropagation()}>

            <img
            src={selectedBrand.images[selectedImageIndex]}
            alt={`${selectedBrand.name} ${selectedImageIndex + 1}`}
            className="max-w-full max-h-[80vh] object-contain"
            style={{ animation: 'fadeIn 0.3s ease-out' }} />


            {/* Navigation Arrows */}
            <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-red-500 transition-colors duration-300"
            style={{
              background: 'rgba(26, 26, 26, 0.8)',
              border: 'none',
              padding: '1rem',
              borderRadius: '50%',
              cursor: 'pointer'
            }}>

              <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">

                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-red-500 transition-colors duration-300"
            style={{
              background: 'rgba(26, 26, 26, 0.8)',
              border: 'none',
              padding: '1rem',
              borderRadius: '50%',
              cursor: 'pointer'
            }}>

              <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2">

                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </div>

          {/* Thumbnails */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-4xl px-4">
            {selectedBrand.images.map((image, index) =>
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImageIndex(index);
            }}
            className={`flex-shrink-0 transition-all duration-300 ${
            index === selectedImageIndex ?
            'ring-2 ring-red-500' :
            'opacity-50 hover:opacity-100'}`
            }
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }}>

                <img
              src={image}
              alt={`Thumbnail ${index + 1}`}
              className="w-20 h-20 object-cover" />

              </button>
          )}
          </div>
        </div>
      }
    </section>);

};

export default PortfolioSection;